// ─────────────────────────────────────────────────────────────────────────────
// Every string the picture editor renders. English defaults, overridable one
// key at a time, the same convention as the file viewer: the catalogues live
// in the apps, so this package owns no copy that has to be translated.
// Placeholders are single braces, filled by `fill`.
// ─────────────────────────────────────────────────────────────────────────────

export interface ImageCropperLabels {
  /** The modal's title. */
  title: string;
  /** How to use it, said once under the title and read out with the picture. */
  instructions: string;
  /** The accessible name of the area that shows the picture and takes the drag. */
  stage: string;
  zoom: string;
  zoomIn: string;
  zoomOut: string;
  /** The slider's spoken value. "{value}" is the zoom as a percentage of "fit". */
  zoomValue: string;
  /** Above the small result. */
  preview: string;
  /** The small result's accessible name. */
  previewAlt: string;
  /** "{width}", "{height}" and "{format}" (JPG, PNG, WebP). */
  output: string;
  /** Said under the preview: what fills space the picture does not reach. */
  fillNote: string;
  reset: string;
  cancel: string;
  confirm: string;
  loading: string;
  decodeError: string;
  exportError: string;
  /** "{edge}" is the source pixels across the chosen part, "{min}" the least that looks sharp. */
  lowResolution: string;
}

export const DEFAULT_LABELS: ImageCropperLabels = {
  title: 'Crop the picture',
  instructions: 'Drag the picture to move it, and zoom with the slider, the mouse wheel or two fingers. With the keyboard, the arrow keys move it and the plus and minus keys zoom.',
  stage: 'Picture position',
  zoom: 'Zoom',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
  zoomValue: '{value}%',
  preview: 'Preview',
  previewAlt: 'How the finished picture looks',
  output: 'Saved as a {width} × {height} {format}.',
  fillNote: 'Space around the picture, and any see-through parts, are filled in with the background colour.',
  reset: 'Reset',
  cancel: 'Cancel',
  confirm: 'Use this picture',
  loading: 'Opening the picture…',
  decodeError: 'This picture could not be opened. Try a JPG, PNG or WebP file.',
  exportError: 'The picture could not be made. Try again, or choose another picture.',
  lowResolution: 'The part you have chosen is only {edge} pixels across, less than {min}, so it will look blurry. Zoom out, or use a larger picture if you have one.'
};

/** Fill `{name}` placeholders; unknown names are left as they are. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}
